import React from 'react';
import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import Button from 'relements/build/esm/ui/button';
import Loader from 'relements/build/esm/decorators/loader';

import connection from '@src/connection';
import {
  SCREEN_TYPES,
  DEFAULT_LANGUAGE,
  AlertTypes,
} from '@src/constants/MogamboConstants';

import { isProduction } from '@src/utils';
import SmallTalkAPI from '@src/api/SmallTalkAPI';
import { userHasPermission } from '@src/actions/_utils';
import { showConfirmationDialog } from '@src/actions/BotActions';
import { PERMISSION_LEVELS } from '@src/const';
import BotStore from '@src/store';
import {
  normalizeAllIntentsFromAPI,
  normalizeBotSaysToAPI,
  normalizeIntentFromAPI,
} from './utils/normalizers';
import SmallTalkNavigation from './components/SmallTalkNavigation';
import SmallTalkHeader from './components/SmallTalkHeader';
import SmallTalkUserSays from './components/SmallTalkUserSays';
import SmallTalkBotSays from './components/SmallTalkBotSays';
import TestBot from '../SubStories/TestBot';

import * as maps from './maps';
import styles from './SmallTalk.scss';

@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
export default class SmallTalk extends React.Component {
  state = {
    intents: Immutable([]),
    intent: Immutable({}),
    smallTalkEdited: false,
    activeId: '',
    testBotDrawerActive: false,
    language: DEFAULT_LANGUAGE,
    currentHybridLanguage: DEFAULT_LANGUAGE,
    botSays: [],
    editedLanguages: {},
  };

  componentDidMount() {
    this.props.activateLoader();
    maps.setCurrentScreen(SCREEN_TYPES.SMALLTALK);
    this.initialize();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.meta.domain.title !== nextProps.meta.domain.title) {
      this.initialize(nextProps.meta.domain, nextProps.currentLanguage);
    } else if (
      this.props.currentLanguage.languageScript !==
      nextProps.currentLanguage.languageScript
    ) {
      this.initialize(nextProps.meta.domain, nextProps.currentLanguage);
    }
  }

  render() {
    const { intents = [], intent = {}, activeId, smallTalkEdited } = this.state;
    const { variants = Immutable([]), sentences = [] } = intent;
    const editingDisabled = !this.isEditable();
    const hasSelfServeLayout = BotStore.getSelfServeLayout();

    return (
      <div
        className={`${styles.smallTalk} ${
          hasSelfServeLayout ? styles.smallTalk_ww_noHeader : ''
        }`}
      >
        <SmallTalkNavigation
          nodes={intents}
          activeId={activeId}
          onChange={this.handleIntentNavigation}
          onToggle={this.handleIntentToggle}
          onTestBotClick={this.openTestBot}
          editingDisabled={editingDisabled}
        />
        <div
          className={`${styles.smallTalkBody} ${
            hasSelfServeLayout ? styles.smallTalkBody_ww : ''
          }`}
        >
          <SmallTalkHeader title={this.state.intent.title} />
          <div className={styles.smallTalkBodyColumns}>
            <SmallTalkUserSays sentences={sentences} />
            <SmallTalkBotSays
              variants={variants.asMutable()}
              onChange={this.handleBotSaysChange}
              editingDisabled={editingDisabled}
              language={this.state.language}
              currentHybridLanguage={this.state.currentHybridLanguage}
              handleTabChange={this.handleTabChange}
              isRephraseResponsesEnabled={this.props.isRephraseResponsesEnabled}
            />
          </div>
          <div className={styles.smallTalkFooter}>
            <Button
              size={Button.SIZES.BIG}
              type={Button.TYPES.PRIMARY}
              onClick={this.save}
              disabled={!smallTalkEdited}
            >
              Save Changes
            </Button>
          </div>
        </div>
        <TestBot
          active={this.state.testBotDrawerActive}
          onClose={this.closeTestBot}
        />
      </div>
    );
  }

  isEditable() {
    return (
      !isProduction() && userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)
    );
  }

  openTestBot = () => {
    this.setState({ testBotDrawerActive: true });
  };

  closeTestBot = () => {
    this.setState({ testBotDrawerActive: false });
  };

  async initialize(
    domain = this.props.meta.domain,
    currentLanguage = this.props.currentLanguage
  ) {
    if (!domain.title) return;
    this.SmallTalkAPI = new SmallTalkAPI(
      domain.title,
      currentLanguage.languageScript
    );
    this.getAllIntents()
      .then(this.getIntent)
      .finally(() => {
        this.props.deactivateLoader();
      });
  }

  async getAllIntents() {
    return new Promise((resolve, reject) => {
      this.SmallTalkAPI.getAllIntents()
        .then((response) => {
          const intents = normalizeAllIntentsFromAPI(response.body.all_intents);
          this.setState(
            { intents, intent: intents[0], activeId: intents[0].title },
            resolve
          );
        })
        .catch((e) => {
          maps.showRequestFailureAlert('Intents Request failed');
          reject(e);
        });
    });
  }

  getIntent = async (intentName = this.state.intent.title) => {
    return new Promise((resolve, reject) => {
      this.SmallTalkAPI.getIntent(intentName)
        .then(({ response }) => {
          const { language, currentHybridLanguage } = this.state;
          const botSays =
            response.body.bot_says?.[language]?.[currentHybridLanguage];
          const body = {
            ...response.body,
            bot_says: botSays,
          };
          const intent = normalizeIntentFromAPI(body, intentName);
          this.setState(
            { intent, activeId: intent.id, botSays: response.body.bot_says },
            resolve
          );
        })
        .catch((e) => {
          maps.showRequestFailureAlert('Intent Request failed');
          reject(e);
        });
    });
  };

  handleBotSaysChange = (changedBotSays) => {
    const {
      editedLanguages,
      language,
      currentHybridLanguage,
      botSays,
    } = this.state;
    const editedLang = editedLanguages;

    if (!this.isEditable())
      return maps.showRequestFailureAlert(
        'You do not have permissions to do this'
      );

    this.setState((state) => ({
      intent: state.intent.set('variants', changedBotSays),
      smallTalkEdited: true,
    }));
    const updatedBotSays = botSays;

    // if language is not present, setting it to empty object
    if (!editedLang[language]) {
      editedLang[language] = {};
    }
    if (!editedLang[language][currentHybridLanguage]) {
      editedLang[language][currentHybridLanguage] = {};
    }
    editedLang[language] = {
      ...editedLang[language],
      [currentHybridLanguage]: changedBotSays.map((message) => {
        return { text: message };
      }),
    };
    if (!updatedBotSays[language]) {
      updatedBotSays[language] = {};
    }

    // updating bot says on change
    updatedBotSays[language][currentHybridLanguage] = changedBotSays.map(
      (message) => {
        return { text: message };
      }
    );
    this.setState({
      editedLanguages: editedLang,
      botSays: updatedBotSays,
    });
  };

  handleTabChange = (botSaysLanguage, hybridLanguage) => {
    const { botSays } = this.state;
    this.setState((state) => ({
      language: botSaysLanguage,
      currentHybridLanguage: hybridLanguage,
      intent: state.intent.set(
        'variants',
        botSays?.[botSaysLanguage]?.[hybridLanguage]?.map(
          (variant) => variant.text
        ) || []
      ),
    }));
  };

  handleIntentToggle = (intent, i) => {
    if (intent.active) this.SmallTalkAPI.disableIntent(intent.title);
    else this.SmallTalkAPI.enableIntent(intent.title);

    this.setState((state) => ({
      intents: state.intents.map((data, index) =>
        i === index ? data.set('active', !intent.active) : data
      ),
    }));
  };

  handleIntentNavigation = (intent, navigateAnyway) => {
    if (this.state.smallTalkEdited && !navigateAnyway) {
      showConfirmationDialog({
        okTitle: 'Discard Changes',
        cancelTitle: 'Cancel',
        title: 'You have unsaved changes',
        message: 'All unsaved changed will be lost',
        onOk: () => this.handleIntentNavigation(intent, true),
        onCancel: () => {},
        okActionType: 'danger',
      });

      return;
    }

    this.setState({ activeId: intent.id, smallTalkEdited: false });
    this.props.activateLoader();
    this.getIntent(intent.title).finally(this.props.deactivateLoader);
  };

  save = () => {
    this.props.activateLoader();

    // edited botSays when hinglish is enabled
    const botSays = this.state.editedLanguages;

    this.SmallTalkAPI.saveIntent(this.state.intent.title, botSays)
      .then(() => {
        this.setState({ smallTalkEdited: false });
        maps.createAlert(
          'Changes changed successfully',
          null,
          AlertTypes.POSITIVE
        );
        this.setState({ editedLanguages: {} });
      })
      .catch(() => maps.showRequestFailureAlert('Error saving changes'))
      .finally(this.props.deactivateLoader);
  };
}

SmallTalk.propTypes = {
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
  currentLanguage: PropTypes.shape({
    languageScript: PropTypes.string,
    languageSource: PropTypes.string,
  }),
  meta: PropTypes.shape({
    domain: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    }),
  }),
};

SmallTalk.defaultProps = {
  currentLanguage: {
    languageScript: 'en',
    languageSource: 'en',
  },
  activateLoader: () => {},
  deactivateLoader: () => {},
  meta: {
    domain: {},
  },
};
